import { TAG_CONFIG, type TagId } from './design-data';

/**
 * Single source of truth mapping a Google Places `type` to a travel
 * category (`TagId`). Both the Review-tab stop color (`placeCategoryTag`
 * below) and the affiliate CTA family (`components/affiliates/PlaceCtaRow`)
 * derive from this one table so the two can't drift.
 *
 * Colors come from the existing category table (`TAG_CONFIG` in
 * `design-data.ts`) — this module invents no new colors.
 */
export const GOOGLE_TYPE_TAG: Record<string, TagId> = {
  // hotel
  lodging: 'hotel', hotel: 'hotel', resort_hotel: 'hotel', motel: 'hotel',
  bed_and_breakfast: 'hotel', hostel: 'hotel', guest_house: 'hotel',
  extended_stay_hotel: 'hotel', inn: 'hotel',
  // nightlife
  night_club: 'nightlife', bar: 'nightlife', pub: 'nightlife', casino: 'nightlife',
  // food
  restaurant: 'food', fine_dining_restaurant: 'food', fast_food_restaurant: 'food',
  meal_takeaway: 'food', meal_delivery: 'food', cafe: 'food', coffee_shop: 'food',
  bakery: 'food', food: 'food', ice_cream_shop: 'food',
  // beach
  beach: 'beach',
  // outdoors
  park: 'outdoors', national_park: 'outdoors', state_park: 'outdoors',
  hiking_area: 'outdoors', garden: 'outdoors', botanical_garden: 'outdoors',
  natural_feature: 'outdoors', campground: 'outdoors', rv_park: 'outdoors',
  scenic_point: 'outdoors', trail_head: 'outdoors',
  // sightseeing
  tourist_attraction: 'sightseeing', museum: 'sightseeing', art_gallery: 'sightseeing',
  aquarium: 'sightseeing', amusement_park: 'sightseeing', theme_park: 'sightseeing',
  zoo: 'sightseeing', historical_landmark: 'sightseeing', monument: 'sightseeing',
  church: 'sightseeing', mosque: 'sightseeing', temple: 'sightseeing',
  synagogue: 'sightseeing', place_of_worship: 'sightseeing', stadium: 'sightseeing',
  castle: 'sightseeing', palace: 'sightseeing', observation_deck: 'sightseeing',
  cultural_landmark: 'sightseeing', landmark: 'sightseeing',
  // transport
  transit_station: 'transport', train_station: 'transport', subway_station: 'transport',
  bus_station: 'transport', light_rail_station: 'transport', airport: 'transport',
  ferry_terminal: 'transport', taxi_stand: 'transport',
};

// Priority when a place carries several typed families — the first match
// wins, so a hotel bar reads as `hotel`, a `night_club` as `nightlife`
// (not food), and a beach as `beach` (not the broader `natural_feature`).
const TAG_PRIORITY: readonly TagId[] = [
  'hotel', 'nightlife', 'food', 'beach', 'outdoors', 'sightseeing', 'transport',
];

/** Normalize a display category like "Fine dining restaurant" back to a
 *  Google-style token ("fine_dining_restaurant") so `fallbackCategory`
 *  can be matched against the same table. */
function normalizeToken(raw: string): string {
  return raw.trim().toLowerCase().replace(/\s+/g, '_');
}

/**
 * Map a place's Google `types` (preferred) or a display `fallbackCategory`
 * string to a `TagId`. Returns `null` when nothing matches (generic
 * `point_of_interest` / `establishment`, or no data) — callers keep their
 * default color in that case.
 */
export function placeCategoryTag(
  types?: string[] | undefined,
  fallbackCategory?: string | undefined,
): TagId | null {
  const tokens = types && types.length > 0
    ? types
    : fallbackCategory
      ? [normalizeToken(fallbackCategory)]
      : [];
  if (tokens.length === 0) return null;
  const found = new Set<TagId>();
  for (const t of tokens) {
    const tag = GOOGLE_TYPE_TAG[t];
    if (tag) found.add(tag);
  }
  for (const tag of TAG_PRIORITY) {
    if (found.has(tag)) return tag;
  }
  return null;
}

/** Category ink color for a resolved tag, from the shared category
 *  palette. `null` tag → `null` (caller falls back to its own accent). */
export function categoryColor(tag: TagId | null): string | null {
  return tag ? TAG_CONFIG[tag].ink : null;
}

export type CtaFamily = 'lodging' | 'attraction' | 'restaurant';

/**
 * Which affiliate CTA family a place earns, derived from the same
 * `GOOGLE_TYPE_TAG` table that drives stop colors so type→family
 * membership lives in one place (consumed by
 * `components/affiliates/PlaceCtaRow`). Match by *any* type in the array.
 * Precedence lodging → attraction → restaurant: when a place IS a hotel,
 * "Check rates" beats "Tickets" (which Viator routes to city-wide tours).
 *
 * Campgrounds / RV parks color as `outdoors` but are *booked* like
 * lodging, so they're pulled into the lodging CTA — the one spot where
 * CTA family intentionally diverges from stop color.
 */
export function ctaFamily(types: string[]): CtaFamily | null {
  let hasHotel = false;
  let hasAttraction = false;
  let hasRestaurant = false;
  let hasCampLodging = false;
  for (const t of types) {
    if (t === 'campground' || t === 'rv_park') hasCampLodging = true;
    const tag = GOOGLE_TYPE_TAG[t];
    if (tag === 'hotel') hasHotel = true;
    else if (tag === 'sightseeing' || tag === 'outdoors') hasAttraction = true;
    else if (tag === 'food' || tag === 'nightlife') hasRestaurant = true;
  }
  if (hasHotel || hasCampLodging) return 'lodging';
  if (hasAttraction) return 'attraction';
  if (hasRestaurant) return 'restaurant';
  return null;
}
